import { useHandleResponse } from '@/utils/useHandleResponse';
import { MinusCircleOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { App, AutoComplete, Button, Checkbox, Col, Divider, Form, Input, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  removeSupervisors,
  updateField,
  updateProps,
  updateSupervisors,
} from '../../../contextRedux/UserGroup/UserGroupModel';
import UserGroupService from '../../../services/userGroup/UserGroupService';
import { RootState } from '../../../store';
import useWindowSize from '../../../utils/useWindowSize';

const UserGroupForm: FC<any> = () => {
  const [form] = useForm();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();
  const dispatch = useDispatch();
  const width = useWindowSize();

  const userGroup = useSelector((state: RootState) => state.models.userGroup);
  const supervisors = useSelector((state: RootState) => state.models.userGroup.supervisors);
  const users = useSelector((state: RootState) => state.store.user.data);

  const initialValues = {
    id: useSelector((state: RootState) => state?.models?.userGroup?.id),
    name: useSelector((state: RootState) => state?.models?.userGroup?.name),
    isActive: useSelector((state: RootState) => state?.models?.userGroup?.isActive),
    supervisors: useSelector((state: RootState) => state?.models?.userGroup.supervisors),
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldsValue = {
      ...form.getFieldsValue(true),
      isActive: e.target.checked,
    };
    form.setFieldsValue(fieldsValue);
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().includes(inputValue.toLowerCase());
  };

  const handleSelectUser = (value: any, index: number): void => {
    const user = users.find((a) => a?.name === value);
    if (user) {
      const userField = form.getFieldValue(['supervisors', index]);

      form.setFieldsValue({
        supervisors: [
          ...form.getFieldValue('supervisors').slice(0, index),
          {
            ...userField,
            userId: user?.id,
            name: user.name,
          },
          ...form.getFieldValue('supervisors').slice(index + 1),
        ],
      });

      dispatch(updateSupervisors({ index, updates: { userId: user?.id, name: user?.name } }));
    }
  };

  const onSaveUserGroup = async () => {
    saveCompanyGroup(userGroup);
  };

  const { mutate: saveCompanyGroup, isPending: _isSavingCompanyGroup } = useMutation({
    mutationFn: (formData: any) => UserGroupService.upsert(userGroup.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, data?.response?.name);
        dispatch(updateProps({ field: 'isModalVisible', value: false }));
        dispatch(updateProps({ field: 'refresh', value: !userGroup.props.refresh }));
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const handleFieldChange = (changedValues: any, allValues: any) => {
    for (const key in changedValues) {
      if (key !== 'supervisors') {
        dispatch(updateField({ field: key, value: changedValues[key] }));
      }
    }
    if (changedValues.supervisors) {
      dispatch(updateField({ field: 'supervisors', value: allValues.supervisors }));
    }
  };

  const handleAddSupervisor = (addFn: any) => {
    const newSupervisors = {
      userId: '',
      name: '',
    };

    const updatedSupervisors = [...supervisors, newSupervisors];
    dispatch(updateField({ field: 'supervisors', value: updatedSupervisors }));

    addFn(newSupervisors);
  };

  const handleRemoveSupervisor = (index: number, removeFn: any) => {
    removeFn(index);
    dispatch(removeSupervisors(index));
  };

  useEffect(() => {
    form.setFieldsValue(userGroup);
  }, [userGroup]);

  return (
    <Form
      form={form}
      name='userGroupForm'
      initialValues={initialValues}
      onValuesChange={handleFieldChange}
      onFinish={onSaveUserGroup}
      layout='vertical'
    >
      <Divider />

      <Row
        gutter={16}
        style={{
          margin: 0,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Col
          xs={24}
          sm={12}
          style={{
            minWidth: width > 700 ? 300 : '100%',
            padding: 0,
            flex: 'none',
          }}
        >
          <Form.Item label='Denumire' name='name' rules={[{ required: true, message: 'Introdu denumire!' }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col
          xs={24}
          sm={12}
          style={{
            minWidth: width > 700 ? 300 : '100%',
            padding: 0,
            flex: 'none',
          }}
        >
          <Form.Item label='Status' name='isActive' valuePropName='checked'>
            <Checkbox onChange={() => handleCheckboxChange} />
          </Form.Item>
        </Col>
      </Row>
      <Row style={{ width: '100%' }}>
        <Col xs={24}>
          <Form.List name='supervisors' initialValue={supervisors}>
            {(fields, { add, remove }) => (
              <>
                {fields?.map((field, index) => (
                  <Row key={field.key} style={{ width: '100%', alignItems: 'center' }}>
                    <Col style={{ width: '90%' }}>
                      <Form.Item
                        {...field}
                        label='Selecteaza Supervizor'
                        name={[field.name, 'user', 'name']}
                        rules={[{ required: true, message: 'Selecteaza un supervizor!' }]}
                      >
                        <AutoComplete
                          options={users?.map((user) => ({
                            value: user?.name || '',
                          }))}
                          onSelect={(value) => handleSelectUser(value, index)}
                          filterOption={handleFilter}
                          placeholder='Alege un supervizor'
                        />
                      </Form.Item>
                    </Col>
                    <Col style={{ width: '10%' }}>
                      <Button
                        type='link'
                        icon={<MinusCircleOutlined />}
                        onClick={() => handleRemoveSupervisor(field.name, remove)}
                      />
                    </Col>
                  </Row>
                ))}
                <Form.Item>
                  <Button type='dashed' onClick={() => handleAddSupervisor(add)} style={{ width: '100%' }}>
                    Adauga un Supervizor
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Col>
      </Row>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Finalizare
        </Button>
      </Form.Item>
    </Form>
  );
};

export default UserGroupForm;
